// Pure helpers for Settings · Industry pack (D-343). No React, no I/O, so
// `__tests__/pack-update-model.test.ts` holds every rule the screen relies on.

export type PreviewChange = "added" | "changed" | "removed" | "missing";
export type PreviewDisposition =
  "applies" | "acceptable" | "customized" | "adopt_only" | "kept" | "pending_module";

export interface PreviewEntry {
  object_kind: string;
  key: string;
  change: PreviewChange;
  fields: readonly string[];
  disposition: PreviewDisposition;
}

export interface KindGroup<E extends PreviewEntry = PreviewEntry> {
  kind: string;
  entries: E[];
}

/** The id one entry is ticked by. A pack ref never holds a newline. */
export function entryId(e: { object_kind: string; key: string }): string {
  return `${e.object_kind}\n${e.key}`;
}

/** Entries grouped by kind, keeping the server's order (seed order, then pack
 * order) for the groups and within each group. */
export function groupByKind<E extends PreviewEntry>(entries: readonly E[]): KindGroup<E>[] {
  const groups: KindGroup<E>[] = [];
  const at = new Map<string, KindGroup<E>>();
  for (const e of entries) {
    let g = at.get(e.object_kind);
    if (g === undefined) {
      g = { kind: e.object_kind, entries: [] };
      at.set(e.object_kind, g);
      groups.push(g);
    }
    g.entries.push(e);
  }
  return groups;
}

/** Only an `acceptable` entry can be ticked; everything else is decided. */
export function isTickable(e: PreviewEntry): boolean {
  return e.disposition === "acceptable";
}

/** The ids every acceptable entry carries, for "tick every change". */
export function allTickable(entries: readonly PreviewEntry[]): Set<string> {
  return new Set(entries.filter(isTickable).map(entryId));
}

/** The POST body's `accept`, from the ticked ids, in the preview's order. A
 * stale id (an entry the refreshed preview no longer lists as acceptable) is
 * dropped here, so a re-read preview can never send a tick it does not show. */
export function acceptList(
  entries: readonly PreviewEntry[],
  ticked: ReadonlySet<string>,
): { kind: string; key: string }[] {
  return entries
    .filter((e) => isTickable(e) && ticked.has(entryId(e)))
    .map((e) => ({ kind: e.object_kind, key: e.key }));
}

/** How many entries an apply adds without asking. */
export function appliesCount(entries: readonly PreviewEntry[]): number {
  return entries.filter((e) => e.disposition === "applies").length;
}

/** Whether Apply can do anything with this selection: the base moves, an entry
 * applies, or a change is ticked. Mirrors the server's PACK_UP_TO_DATE rule. */
export function canApply(
  preview: { update_available: boolean; changes: readonly PreviewEntry[] },
  ticked: ReadonlySet<string>,
): boolean {
  return (
    preview.update_available ||
    appliesCount(preview.changes) > 0 ||
    acceptList(preview.changes, ticked).length > 0
  );
}

/** The short form of a content digest the screen shows (12 hex characters). */
export function shortDigest(digest: string): string {
  return digest.slice(0, 12);
}

/** The problem codes the apply answers with that the screen names. */
export const APPLY_PROBLEM_CODES = [
  "PACK_UPDATE_STALE",
  "PACK_UP_TO_DATE",
  "PACK_UPDATE_NOT_ACCEPTABLE",
  "PACK_INSTALL_IN_PROGRESS",
] as const;

export type ApplyProblemCode = (typeof APPLY_PROBLEM_CODES)[number];

export function applyProblemKey(code: string | undefined): ApplyProblemCode | "generic" {
  return (APPLY_PROBLEM_CODES as readonly string[]).includes(code ?? "")
    ? (code as ApplyProblemCode)
    : "generic";
}

/** A refusal after which the preview must be read again before another try. */
export function refusalRefreshesPreview(code: string | undefined): boolean {
  return code === "PACK_UPDATE_STALE" || code === "PACK_UPDATE_NOT_ACCEPTABLE";
}
